import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";

export const FREE_FEATURES = ["Unlimited trips", "Invites & RSVPs (travelers join with a free account)", "Personal traveler portals", "Itinerary with optional activities", "Individual cost breakdowns",
  "Manual payments, deposits & installments", "Lodging list, polls, packing lists & tasks", "Flight & arrival details", "Readiness checklist, documents & public guide"];

export function PlanCards({ plans, labels, current, action }) {
  if (!plans) return null;
  return (
    <div className="grid gap-5 md:grid-cols-3">
      {["free", "premium", "pro"].map((k, i) => {
        const p = plans[k];
        const hl = k === "premium";
        return (
          <div key={k} className={`card relative flex flex-col p-6 ${hl ? "ring-2 ring-orange-500" : ""}`} data-testid={`plan-card-${k}`}>
            {hl && <span className="pill absolute -top-3 left-6 bg-orange-500 text-white">Most flexible</span>}
            <h3 className="text-xl font-bold">{p.name}</h3>
            <p className="mt-2"><span className="font-display text-4xl font-extrabold">${p.price_usd}</span><span className="text-slate-500">/month</span></p>
            <p className="mt-2 text-sm text-slate-600">Up to {p.max_travelers} travelers per trip · {p.max_co_organizers ? `${p.max_co_organizers} co-organizers` : "no co-organizers"}</p>
            <p className="mt-1 text-xs text-slate-500" data-testid={`plan-fee-${k}`}>Card payments from travelers: {p.platform_fee_bps / 100}% Triply service fee</p>
            <ul className="mt-5 flex-1 space-y-2 text-sm">
              {(i === 0 ? FREE_FEATURES : [`Everything in ${i === 1 ? "Free" : "Premium"}`, `${p.ai_messages_per_month} AI concierge messages / month`, ...(i === 1 ? ["Co-organizers"] : []), ...p.features.filter((f) => i === 1 || !plans.premium.features.includes(f)).map((f) => labels[f])]).map((f) => (
                <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />{f}</li>
              ))}
            </ul>
            <div className="mt-6">{action ? action(k, current) : null}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function Pricing() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/billing/plans").then((r) => setData(r.data)); }, []);
  return (
    <PublicLayout>
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">Simple, feature-based pricing</h1>
        <p className="mt-4 max-w-2xl text-slate-600">Create as many trips as you like for free. Upgrade only if you want automation and bigger groups. If you downgrade, your trips and records stay exactly where they are.</p>
        <div className="mt-10">
          <PlanCards plans={data?.plans} labels={data?.feature_labels || {}} action={(k) => (
            <Link to={k === "free" ? "/register" : "/billing"} className={`block rounded-full px-4 py-2.5 text-center text-sm font-semibold ${k === "free" ? "bg-slate-900 text-white" : "bg-orange-500 text-white hover:bg-orange-600"}`} data-testid={`pricing-cta-${k}`}>
              {k === "free" ? "Start free" : `Choose ${data.plans[k].name}`}
            </Link>
          )} />
        </div>
        <p className="mt-8 text-sm text-slate-500">Prices in USD, billed monthly, cancel anytime. Applicable taxes are calculated at checkout. Triply doesn't take payments between travelers and organizers.</p>
      </section>
    </PublicLayout>
  );
}
